import { defineStore } from 'pinia'
import { reactive, ref, computed } from 'vue'
import type { AxiosResponse } from 'axios'

import { GuestService } from './service'
import type { EveLinkRequestPayload, GuestNotificationDto } from './service'

import type { EventLp } from './types'
import type { QueryParams } from '@/services/types'
import type {
  EventType,
  ParticipantType,
} from '@/modules/expert/events/schemas/schemas'

/** ===== Types ===== */
export type EveLinkRequestAction = 'REPORT' | 'EDIT' | 'DELETE'

export type EveLinkRequestDto = {
  action: EveLinkRequestAction
  createdDate: string
  message: string | null
}

export type EveLinkRequest = EveLinkRequestAction | EveLinkRequestDto

export type EveLinkDto = {
  id: string
  createdDate: string
  eventName: string
  pending: boolean
  userName: string
  userEmail: string
  companyName: string
  companyLogo?: string
  requests?: EveLinkRequest[]
}

export type ProducerLinkStatus = 'active' | 'pending'
export type LgpdRequestStatus = 'none' | 'report' | 'edit' | 'delete'

export type FavoriteProducerRow = {
  id: string
  name: string
  status: ProducerLinkStatus
  request: LgpdRequestStatus
  requests?: LgpdRequestStatus[]
  companyLogo?: string
  logoUrl?: string
  linkedAt?: string | null
  eventName?: string | null
}

export const useGuestStore = defineStore('guest', () => {
  /** ===== State ===== */
  const selectedEventLp = ref<EventLp>()
  const eventList = ref<EventType[]>([])
  const searchParams: QueryParams = reactive({})
  const selectedEvent = ref<EventType>()
  const loading = ref<boolean>(false)
  const eventsListPage = ref(0)
  const participantsList = ref<ParticipantType[]>([])

  const favoriteLinks = ref<EveLinkDto[]>([])
  const favoriteProducers = ref<FavoriteProducerRow[]>([])
  const favoritesLoading = ref(false)

  /** ===== Notifications ===== */
  const notifications = ref<GuestNotificationDto[]>([])
  const notificationsLoading = ref(false)
  const notificationsLoaded = ref(false)

  const unreadNotificationsCount = computed(() => {
    return notifications.value.filter((item) => !item.read).length
  })

  const hasUnreadNotifications = computed(() => {
    return unreadNotificationsCount.value > 0
  })

  async function getEventLp(eventId: string) {
    const res = await GuestService.getEventLp(eventId)
    const { data, error } = res
    if (!data || error) return { error }
    selectedEventLp.value = data
    return res
  }

  async function getEventLpDeslogado(eventId: string) {
    const res = await GuestService.getEventLpDeslogado(eventId)
    const { data, error } = res
    if (!data || error) return { error }
    selectedEventLp.value = data
    return res
  }

  async function getEventsList(params?: QueryParams) {
    eventList.value = []
    const response: AxiosResponse<EventType[]> = await GuestService.list(params)
    if (!response.data) return { error: 'No data returned' }

    eventList.value = response.data.map((event) => ({
      ...event,
      startDate:
        event.startDate && !isNaN(new Date(event.startDate).getTime())
          ? new Date(event.startDate).toISOString()
          : '',
      endDate:
        event.endDate && !isNaN(new Date(event.endDate).getTime())
          ? new Date(event.endDate).toISOString()
          : '',
    }))

    return response
  }

  async function getParticipantsList(eventId: string) {
    participantsList.value = []
    const res = await GuestService.getParticipantsList(eventId)
    const { data, error } = res
    if (!data || error) return { error }
    participantsList.value = data
    return res
  }

  function toReqAction(r: EveLinkRequest): EveLinkRequestAction {
    return typeof r === 'string' ? r : r.action
  }

  function toReqCreatedDate(r: EveLinkRequest): string {
    return typeof r === 'string' ? '' : r.createdDate
  }

  function actionToKind(
    action: EveLinkRequestAction
  ): Exclude<LgpdRequestStatus, 'none'> {
    if (action === 'REPORT') return 'report'
    if (action === 'EDIT') return 'edit'
    return 'delete'
  }

  function getAllRequestKinds(
    requests?: EveLinkRequest[]
  ): Exclude<LgpdRequestStatus, 'none'>[] {
    if (!requests?.length) return []

    const order: Record<EveLinkRequestAction, number> = {
      EDIT: 1,
      REPORT: 2,
      DELETE: 3,
    }

    const actions = requests.map(toReqAction)
    const uniqueOrdered = Array.from(new Set(actions)).sort(
      (a, b) => (order[a] ?? 99) - (order[b] ?? 99)
    )

    return uniqueOrdered.map(actionToKind)
  }

  function getLastRequestAction(
    requests?: EveLinkRequest[]
  ): EveLinkRequestAction | undefined {
    if (!requests?.length) return undefined

    const hasDates = requests.some(
      (r) => typeof r !== 'string' && !!r.createdDate
    )
    if (!hasDates) return toReqAction(requests[requests.length - 1]!)

    const sorted = [...requests].sort((a, b) => {
      const ta = new Date(toReqCreatedDate(a) || 0).getTime()
      const tb = new Date(toReqCreatedDate(b) || 0).getTime()
      return ta - tb
    })

    return toReqAction(sorted[sorted.length - 1]!)
  }

  function mapLinkToFavoriteRow(link: EveLinkDto): FavoriteProducerRow {
    const status: FavoriteProducerRow['status'] = link.pending
      ? 'pending'
      : 'active'

    const requests = getAllRequestKinds(link.requests)
    const lastAction = getLastRequestAction(link.requests)

    const request: FavoriteProducerRow['request'] =
      lastAction === 'DELETE'
        ? 'delete'
        : lastAction === 'EDIT'
          ? 'edit'
          : lastAction === 'REPORT'
            ? 'report'
            : 'none'

    return {
      id: link.id,
      name: link.companyName,
      status,
      request,
      requests,
      companyLogo: link.companyLogo,
      logoUrl: link.companyLogo,
      linkedAt: link.createdDate,
      eventName: link.eventName,
    }
  }

  function mergeEveLinksById(list: EveLinkDto[]): EveLinkDto[] {
    const map = new Map<string, EveLinkDto>()

    for (const item of list) {
      const prev = map.get(item.id)

      if (!prev) {
        map.set(item.id, { ...item, requests: [...(item.requests ?? [])] })
        continue
      }

      const mergedRequests = [
        ...(prev.requests ?? []),
        ...(item.requests ?? []),
      ]

      const reqKey = (r: EveLinkRequest) => {
        if (typeof r === 'string') return `S:${r}`
        return `D:${r.action}-${r.createdDate}-${r.message ?? ''}`
      }

      const uniqueRequests = Array.from(
        new Map(mergedRequests.map((r) => [reqKey(r), r])).values()
      ).sort((a, b) => {
        const ta = new Date(toReqCreatedDate(a) || 0).getTime()
        const tb = new Date(toReqCreatedDate(b) || 0).getTime()
        return ta - tb
      })

      const newest =
        new Date(item.createdDate).getTime() >=
        new Date(prev.createdDate).getTime()
          ? item
          : prev

      const oldest = newest === item ? prev : item

      map.set(item.id, {
        ...oldest,
        ...newest,
        requests: uniqueRequests,
      })
    }

    return Array.from(map.values())
  }

  /** ===== Favorites ===== */
  async function getFavoriteProducers(params?: QueryParams) {
    favoritesLoading.value = true
    try {
      const res = await GuestService.listEveLinks(params)

      const raw = (res.data ?? []) as EveLinkDto[]
      const merged = mergeEveLinksById(raw)

      favoriteLinks.value = merged
      favoriteProducers.value = merged.map(mapLinkToFavoriteRow)

      return res
    } finally {
      favoritesLoading.value = false
    }
  }

  async function requestLgpd(
    eveLinkId: string,
    payload: EveLinkRequestPayload
  ) {
    const res = await GuestService.requestEveLink(eveLinkId, payload)
    await getFavoriteProducers()
    return res
  }

  /** ===== Notifications ===== */
  async function getNotifications(params?: QueryParams) {
    notificationsLoading.value = true

    try {
      const res = await GuestService.listNotifications(params)
      notifications.value = (res.data ?? []) as GuestNotificationDto[]
      notificationsLoaded.value = true
      return res
    } finally {
      notificationsLoading.value = false
    }
  }

  async function setNotificationRead(notificationId: string, read: boolean) {
    const res = await GuestService.setNotificationRead(notificationId, read)

    notifications.value = notifications.value.map((item) =>
      item.id === notificationId ? { ...item, read } : item
    )

    return res
  }

  async function setAllNotificationsRead(read: boolean) {
    const res = await GuestService.setAllNotificationsRead(read)

    notifications.value = notifications.value.map((item) => ({
      ...item,
      read,
    }))

    return res
  }

  async function readNotification(notificationId: string) {
    return await setNotificationRead(notificationId, true)
  }

  async function unreadNotification(notificationId: string) {
    return await setNotificationRead(notificationId, false)
  }

  async function readAllNotifications() {
    return await setAllNotificationsRead(true)
  }

  async function unreadAllNotifications() {
    return await setAllNotificationsRead(false)
  }

  /** ===== Exports ===== */
  return {
    // state
    selectedEvent,
    loading,
    searchParams,
    eventsListPage,
    eventList,
    selectedEventLp,
    participantsList,

    favoriteLinks,
    favoriteProducers,
    favoritesLoading,

    notifications,
    notificationsLoading,
    notificationsLoaded,
    unreadNotificationsCount,
    hasUnreadNotifications,

    // actions
    getEventLp,
    getEventLpDeslogado,
    getEventsList,
    getParticipantsList,
    getFavoriteProducers,
    requestLgpd,

    getNotifications,
    setNotificationRead,
    setAllNotificationsRead,
    readNotification,
    unreadNotification,
    readAllNotifications,
    unreadAllNotifications,
  }
})
